import { taxIdWord } from "../../../model/steuerbegriffe";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { CURRENCIES } from "@talentkontor/domain";
import { useStaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { Dialog, PillView, useAction } from "../../../components/ui";
import { AdressVorschlag } from "../../../components/AdressVorschlag";
import { ustIdPruefenMitMeldung } from "../../../components/UstIdPruefung";
import { CONFLICT_TEXT, createBrand, deleteBrand, deleteContact, findBrandDuplicates, saveBrandNote, saveContact, updateBrand } from "./data";
import { useBrandNote, useFreshBrand } from "./hooks";
import {
  brandFormFrom, brandTermsFallback, CONTACT_ROLES, contactFormFrom, contactRoles, countryOptions, languageForCountry, pill, registerPlaceholder, usageOfBrand, usageTotal,
  validateBrandForm, validateContact, vatHint, zoneHint,
  type Base, type BrandForm, type BrandFull, type ContactForm, type ContactRow, type FormErrors,
} from "./model";
import { ConfirmAction, SelectField, TextField } from "./parts";
import { istCreatorKonto, t, ta } from "../../../lib/sprache";

/**
 * Sprache der Marke: Rechnungsbeleg, Begleitmail und Mahnung nutzen sie. Ohne Angabe nimmt die Mail die Sprache der Agentur,
 * der Beleg entscheidet nach Land; deshalb belegt eine neue Marke die Sprache nach Land vor.
 */
const LANGUAGES = [
  { value: "", get label() { return ta("Nicht festgelegt (Mail wie die Agentur, Beleg nach Land)", "Not set (email like the agency, documents by country)", "Nicht festgelegt (Mail wie dein Konto, Beleg nach Land)", "Not set (email like your account, documents by country)"); } },
  { value: "de", get label() { return t("Deutsch", "German"); } },
  { value: "en", get label() { return t("Englisch", "English"); } },
];
const KINDS = [{ value: "brand", get label() { return t("Marke", "Brand"); } }, { value: "agency", get label() { return t("Agentur (betreut Marken)", "Agency (manages brands)"); } }];

export function orgCountryOf(base: Base): string | null {
  return (base.settings.country as string | null | undefined) ?? null;
}

/**
 * Marke anlegen oder bearbeiten (AD-083 … AD-086): Allgemein, Rechnungsdaten, Agentur der Marke, Kontakte, interne Notiz.
 * Beim Bearbeiten wird die Marke frisch geladen; eine zwischenzeitliche Änderung meldet das Speichern als Konflikt.
 */
export function BrandDialog({ brand, base, contacts, onClose }: { brand: BrandFull | null; base: Base; contacts: readonly ContactRow[]; onClose: () => void }) {
  const ws = useStaffWorkspace();
  const fresh = useFreshBrand(ws.org.org_id, brand?.id ?? null);
  const [version, setVersion] = useState(0);
  return (
    <Dialog open onClose={onClose} title={brand ? t(`${brand.name} bearbeiten`, `Edit ${brand.name}`) : t("Marke anlegen", "Create brand")} wide>
      {brand && fresh.isPending && <p className="muted">{t("Wird geladen …", "Loading …")}</p>}
      {brand && !fresh.isPending && (fresh.error || !fresh.data) && (
        <p className="tk-error" role="alert">{t("Diese Marke wurde nicht gefunden oder ist für dein Konto nicht freigegeben.", "This brand was not found or is not available to your account.")}</p>
      )}
      {(!brand || fresh.data) && (
        <BrandFormView
          key={`${fresh.data?.updated_at ?? "neu"}-${version}`} brand={brand ? fresh.data ?? null : null} base={base} contacts={contacts} onClose={onClose}
          onReload={async () => { await fresh.refetch(); setVersion((v) => v + 1); }}
        />
      )}
    </Dialog>
  );
}

function BrandFormView({ brand, base, contacts, onClose, onReload }: {
  brand: BrandFull | null; base: Base; contacts: readonly ContactRow[]; onClose: () => void; onReload: () => Promise<void>;
}) {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const navigate = useNavigate();
  const orgCountry = orgCountryOf(base);
  const [form, setForm] = useState<BrandForm>(() => brandFormFrom(brand, { country: orgCountry }));
  const [errors, setErrors] = useState<FormErrors<BrandForm>>({});
  const [question, setQuestion] = useState<string | null>(null);
  const [first, setFirst] = useState<ContactForm>(() => contactFormFrom(null));
  const [firstErrors, setFirstErrors] = useState<FormErrors<ContactForm>>({});
  const [dups, setDups] = useState<{ id: string; name: string; reason: string }[] | null>(null);
  const note = useBrandNote(orgId, brand?.id ?? null);
  const [noteText, setNoteText] = useState<string | null>(null);
  const { busy, error, run } = useAction();
  const set = <K extends keyof BrandForm>(k: K, v: BrandForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const live = validateBrandForm(form, orgCountry);
  const agencies = base.brands.filter((b) => b.kind === "agency" && b.id !== brand?.id);
  const representsOthers = !!brand && base.brands.some((b) => b.represented_by === brand.id);
  const represented = brand ? base.brands.filter((b) => b.represented_by === brand.id) : [];
  const usage = brand ? usageOfBrand(base, brand.id) : null;
  // Rückfall wie Rechnung und Datenbank: Zahlungsziel für Marken, sonst allgemeines (SAAS-R3-1)
  const termsDefault = brandTermsFallback(base.settings);
  const noteValue = noteText ?? note.data?.note ?? "";

  async function save(e?: FormEvent, confirmed = false, allowDuplicate = false) {
    e?.preventDefault();
    const r = validateBrandForm(form, orgCountry);
    setErrors(r.errors);
    if (!r.patch) return;
    if (r.question && !confirmed) {
      setQuestion(r.question);
      return;
    }
    setQuestion(null);
    const patch = r.patch;
    // Gleiche Marke unter anderem Anzeigenamen? (rechtlicher Name oder USt-IdNr.)
    if (!allowDuplicate) {
      const found = await findBrandDuplicates(api, orgId, { name: patch.name, legalName: patch.legal_name, vatId: patch.vat_id, exclude: brand?.id ?? null }).catch(() => []);
      // Auch der gleiche Anzeigename fragt nach; bewusst angelegt wird er mit unterschiedlichem rechtlichen Namen (Befund 29)
      if (found.length) {
        setDups(found);
        return;
      }
    }
    setDups(null);
    if (brand) {
      const ok = await run("speichern", async () => {
        const b = await updateBrand(api, brand.id, patch, { expectedUpdatedAt: brand.updated_at });
        if (noteText !== null && noteText !== (note.data?.note ?? "")) await saveBrandNote(api, orgId, brand.id, noteText);
        return b;
      }, t("Marke gespeichert.", "Brand saved."));
      if (ok) {
        onClose();
        // Steuerprüfung L6: USt-IdNr. im Hintergrund bei VIES prüfen (sperrt nie); das Ergebnis kommt als eigene Meldung
        if (patch.vat_id) void ustIdPruefenMitMeldung(orgId, patch.vat_id, brand.id);
      }
      return;
    }
    const fc = first.name.trim() || first.email.trim() ? validateContact(first) : null;
    setFirstErrors(fc?.errors ?? {});
    if (fc && !fc.patch) return;
    const created = await run("speichern", async () => {
      const b = await createBrand(api, orgId, patch);
      if (fc?.patch) await saveContact(api, orgId, b.id, fc.patch);
      if (noteText) await saveBrandNote(api, orgId, b.id, noteText);
      return b;
    }, t("Marke angelegt.", "Brand created."));
    if (created) {
      onClose();
      if (patch.vat_id) void ustIdPruefenMitMeldung(orgId, patch.vat_id, created.id);
      navigate(`/agentur/marken/${created.id}`);
    }
  }

  const conflict = !!error && error.includes(CONFLICT_TEXT());
  return (
    <>
      <form id="marke-form" className="stack" onSubmit={(e) => void save(e)} noValidate>
        <p>{t("Anzeigename intern, rechtlicher Name für Rechnung und Vertrag.", "Display name internally, legal name for invoice and contract.")}</p>

        <h3>{t("Allgemein", "General")}</h3>
        <div className="grid tk-oben">
          <TextField id="bf-name" label={t("Anzeigename", "Display name")} required value={form.name} onChange={(v) => set("name", v)} error={errors.name} hint={t("Wird im Portal angezeigt und kann vom rechtlichen Namen abweichen.", "Shown in the portal and can differ from the legal name.")} />
          <SelectField id="bf-art" label={t("Art", "Type")} value={form.kind} onChange={(v) => set("kind", v as BrandForm["kind"])} options={KINDS} disabled={representsOthers}
            hint={representsOthers ? t("Diese Agentur betreut Marken; die Art bleibt deshalb „Agentur“.", "This agency manages brands; the type therefore remains “Agency”.") : undefined} />
          <TextField id="bf-rechtlich" label={t("Rechtlicher Firmenname", "Legal company name")} value={form.legal_name} onChange={(v) => set("legal_name", v)} hint={t("Erscheint auf Rechnung und Vertrag.", "Appears on invoice and contract.")} />
          <TextField id="bf-strasse" label={t("Straße und Hausnummer", "Street and house number")} value={form.street} onChange={(v) => set("street", v)} />
          <TextField id="bf-plz" label="PLZ" value={form.postal_code} onChange={(v) => set("postal_code", v)} />
          <TextField id="bf-ort" label={t("Ort", "City")} value={form.city} onChange={(v) => set("city", v)} />
          <AdressVorschlag text={form.street} onPick={(a) => { set("street", a.street); if (a.postal_code) set("postal_code", a.postal_code); set("city", a.city); if (a.country) set("country", a.country); }} />
          <SelectField id="bf-land" label={t("Land", "Country")} value={form.country} hint={zoneHint(form.country, orgCountry)}
            onChange={(v) => { set("country", v); if (!brand && form.language === languageForCountry(form.country)) set("language", languageForCountry(v)); }}
            options={[{ value: "", label: t("Nicht angegeben", "Not specified") }, ...countryOptions().map((c) => ({ value: c.code, label: c.name }))]} />
        </div>

        <h3>{t("Rechnungsdaten", "Invoice details")}</h3>
        <div className="grid tk-oben">
          <TextField id="bf-rechnungsmail" label={t("Rechnungs-E-Mail", "Invoice email")} type="email" inputMode="email" value={form.invoice_email} onChange={(v) => set("invoice_email", v)} error={errors.invoice_email} />
          <TextField id="bf-ust" label={form.country ? taxIdWord(form.country) : t("USt-IdNr. / UID", "VAT ID / UID")} value={form.vat_id} onChange={(v) => set("vat_id", v)}
            hint={vatHint(form.vat_id, form.country) ?? live.hints.find((h) => h.includes("USt-IdNr.")) ?? t("Pflicht für Rechnungen ins EU-Ausland; wird im Format des Landes geprüft.", "Required for invoices to other EU countries; checked in the country's format.")} />
          <TextField id="bf-register" label={form.country === "AT" ? "Firmenbuch" : t("Handelsregister", "Commercial register")} placeholder={registerPlaceholder(form.country)} value={form.register_info} onChange={(v) => set("register_info", v)} />
          <TextField id="bf-frist" label={t("Zahlungsziel in Tagen", "Payment term in days")} inputMode="numeric" value={form.payment_terms_days} onChange={(v) => set("payment_terms_days", v)} error={errors.payment_terms_days}
            placeholder={termsDefault != null ? ta(`${termsDefault} (Agentur-Standard für Marken)`, `${termsDefault} (agency default for brands)`, `${termsDefault} (dein Standard für Marken)`, `${termsDefault} (your default for brands)`) : ta("Agentur-Standard für Marken", "Agency default for brands", "Dein Standard für Marken", "Your default for brands")} hint={t("Tage ab Versand der Rechnung. Leer = Zahlungsziel für Marken aus den Einstellungen.", "Days from sending the invoice. Empty = payment term for brands from the settings.")} />
          <SelectField id="bf-waehrung" label={t("Währung", "Currency")} value={form.default_currency} onChange={(v) => set("default_currency", v)}
            options={[{ value: "", label: ta(`Agentur-Standard (${base.currency})`, `Agency default (${base.currency})`, `Dein Standard (${base.currency})`, `Your default (${base.currency})`) }, ...CURRENCIES.map((c) => ({ value: c, label: c }))]} />
          <SelectField id="bf-sprache" label={t("Sprache", "Language")} value={form.language} onChange={(v) => set("language", v)} options={LANGUAGES} hint={t("Sprache von Rechnungen, Mahnungen und Mails an diese Marke.", "Language of invoices, dunning notices and emails to this brand.")} />
          {/* Studio (Creator-Konto): es gibt nur den direkten Weg an die Marke */}
          {form.kind === "brand" && !istCreatorKonto() && (
            <SelectField id="bf-weg" label={t("Abrechnungsweg", "Invoicing route")} value={form.invoice_route} onChange={(v) => set("invoice_route", v as BrandForm["invoice_route"])}
              options={[
                { value: "", label: t(`Agentur-Standard (${base.settings.invoice_route === "direct_to_brand" ? t("Creator direkt an die Marke", "Creator directly to the brand") : t("über die Agentur", "via the agency")})`, `Agency default (${base.settings.invoice_route === "direct_to_brand" ? t("Creator direkt an die Marke", "Creator directly to the brand") : t("über die Agentur", "via the agency")})`) },
                { value: "via_agency", label: t("Über die Agentur: wir stellen der Marke in Rechnung", "Via the agency: we invoice the brand") },
                { value: "direct_to_brand", label: t("Creator direkt an die Marke, wir berechnen dem Creator die Provision", "Creator directly to the brand, we invoice the creator for the commission") },
              ]}
              hint={t("Gilt für neue Kooperationen mit dieser Marke; jede Kooperation kann abweichen.", "Applies to new deals with this brand; each deal can differ.")} />
          )}
        </div>
        <label className="row">
          <input type="checkbox" checked={form.is_business} onChange={(e) => set("is_business", e.target.checked)} />
          {t("Unternehmen (nicht privat)", "Company (not private)")}</label>

        <h3>{t("E-Rechnung", "E-invoice")}</h3>
        <label className="row">
          <input type="checkbox" checked={form.e_invoice} onChange={(e) => set("e_invoice", e.target.checked)} aria-describedby="bf-erechnung-hinweis" />
          {t("E-Rechnung (XRechnung) mitsenden", "Also send an e-invoice (XRechnung)")}</label>
        <small id="bf-erechnung-hinweis">
          {t("Die Rechnungsmail an ", "The invoice email to ")}{form.kind === "agency" ? t("diese Agentur", "this agency") : t("diese Marke", "this brand")} {t("trägt dann neben dem PDF die E-Rechnung als XML (XRechnung nach EN 16931), die Buchhaltungssoftware und Behörden direkt einlesen. Fehlt dafür eine Angabe, geht die Rechnung nicht raus und du siehst, was fehlt.", "then carries the e-invoice as XML (XRechnung under EN 16931) in addition to the PDF, which accounting software and authorities read directly. If a detail is missing, the invoice does not go out and you see what is missing.")}
          {" "}{t("Ohne Haken gilt die Vorgabe: Rechnungen an Unternehmen in Deutschland gehen ab dem Stichtag der E-Rechnungspflicht (ohne Eintrag unter Einstellungen · Gutschriften ab 1. Januar 2027) automatisch als XRechnung.", "Without the tick the default applies: invoices to businesses in Germany automatically go out as an XRechnung from the date of the e-invoicing obligation (without an entry under Settings · Self-billing from 1 January 2027).")}</small>
        <div className="grid tk-oben">
          <TextField id="bf-kaeuferreferenz" label={t("Käuferreferenz / Leitweg-ID", "Buyer reference / Leitweg-ID")} value={form.buyer_reference} onChange={(v) => set("buyer_reference", v)} error={errors.buyer_reference}
            hint={t("Bei öffentlichen Auftraggebern Pflicht (Leitweg-ID). Steht auf der Rechnung als „Ihre Referenz“; eine Referenz an der Kooperation geht vor. Leer: die Rechnungsnummer.", "Mandatory for public-sector clients (Leitweg-ID). Appears on the invoice as “Your reference”; a reference on the deal takes precedence. Empty: the invoice number.")} />
        </div>

        {form.kind === "brand" && (
          <>
            <h3>{t("Agentur der Marke", "The brand's agency")}</h3>
            <SelectField id="bf-agentur" label={t("Kontakt läuft über", "Contact goes through")} value={form.represented_by} onChange={(v) => { set("represented_by", v); if (!v) set("invoice_via_agency", false); }}
              options={[{ value: "", label: t("Direkt über die Marke", "Directly via the brand") }, ...agencies.map((a) => ({ value: a.id, label: a.name }))]}
              hint={agencies.length ? undefined : t("Lege die Agentur zuerst als eigenen Eintrag mit der Art „Agentur“ an.", "First create the agency as its own entry with the type “Agency”.")} />
            <label className="row">
              <input type="checkbox" checked={form.invoice_via_agency} disabled={!form.represented_by} onChange={(e) => set("invoice_via_agency", e.target.checked)}
                aria-describedby={errors.invoice_via_agency ? "bf-via-fehler" : "bf-via-hinweis"} />
              {t("Rechnung an die Agentur statt an die Marke", "Invoice to the agency instead of the brand")}</label>
            <small id="bf-via-hinweis">{t("Neue Kooperationen übernehmen dann Name und Anschrift der Agentur als Rechnungsempfänger; der Versand geht an die Agentur.", "New deals then take the agency's name and address as invoice recipient; sending goes to the agency.")}</small>
            {errors.invoice_via_agency && <small id="bf-via-fehler" className="tk-field-error">{errors.invoice_via_agency}</small>}
          </>
        )}

        {!brand && (
          <>
            <h3>{t("Erster Kontakt", "First contact")}</h3>
            <div className="grid tk-oben">
              <TextField id="bf-k-name" label="Name" value={first.name} onChange={(v) => setFirst((f) => ({ ...f, name: v }))} error={firstErrors.name} />
              <TextField id="bf-k-rolle" label={t("Rolle", "Role")} placeholder={t("Marketing, Einkauf …", "Marketing, purchasing …")} value={first.role} onChange={(v) => setFirst((f) => ({ ...f, role: v }))} />
              <TextField id="bf-k-mail" label={t("E-Mail", "Email")} type="email" inputMode="email" value={first.email} onChange={(v) => setFirst((f) => ({ ...f, email: v }))} error={firstErrors.email} />
              <TextField id="bf-k-telefon" label={t("Telefon", "Phone")} type="tel" inputMode="tel" value={first.phone} onChange={(v) => setFirst((f) => ({ ...f, phone: v }))} />
            </div>
            <RolesField roles={first.roles} onChange={(roles) => setFirst((f) => ({ ...f, roles, is_invoice_recipient: roles.includes("invoice") }))} idPrefix="bf-k" />
          </>
        )}

        <h3>{t("Interne Notiz", "Internal note")}</h3>
        <label>
          {t("Notiz", "Note")}<textarea value={noteValue} onChange={(e) => setNoteText(e.target.value)} placeholder={t("Briefing-Vorlieben, Zuständigkeiten, Bestellnummern …", "Briefing preferences, responsibilities, order numbers …")} aria-describedby="bf-notiz-hinweis" />
          <small id="bf-notiz-hinweis">{ta("Nur für das Team sichtbar, nie für Creator oder Marke.", "Only visible to the team, never to creators or the brand.", "Nur für dich sichtbar, nie für die Marke.", "Only visible to you, never to the brand.")}</small>
        </label>
      </form>

      {brand && (
        <section className="stack" aria-labelledby="bf-kontakte">
          <h3 id="bf-kontakte">{t("Kontakte", "Contacts")}</h3>
          <ContactsEditor brandId={brand.id} contacts={contacts.filter((c) => c.brand_id === brand.id)} />
        </section>
      )}

      {dups && (
        <div className="notice stack" role="alert">
          <p>
            <strong>{t("Gibt es die Marke schon?", "Does the brand already exist?")}</strong> {dups.map((d) => t(`„${d.name}“ (gleiche${d.reason === "vat_id" ? t(" USt-IdNr.", " VAT ID") : d.reason === "name" ? t("r Anzeigename", " display name") : t("r rechtlicher Name", " legal name")})`, `“${d.name}” (same${d.reason === "vat_id" ? t(" USt-IdNr.", " VAT ID") : d.reason === "name" ? t("r Anzeigename", " display name") : t("r rechtlicher Name", " legal name")})`)).join(", ")}{t(". Nutze den vorhandenen Eintrag, damit Kooperationen und Rechnungen nicht auf zwei Marken zerfallen. Brauchst du bewusst einen zweiten Eintrag (etwa zwei Gesellschaften als getrennte Rechnungsempfänger), unterscheide sie über den rechtlichen Namen.", ". Use the existing entry so that deals and invoices are not split across two brands. If you deliberately need a second entry (for example two companies as separate invoice recipients), distinguish them by the legal name.")}</p>
          <div className="row">
            <button type="button" onClick={() => void save(undefined, true, true)}>{t("Trotzdem speichern", "Save anyway")}</button>
            <button type="button" onClick={() => setDups(null)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      )}
      {question && (
        <div className="notice stack" role="alert">
          <p>{question}</p>
          <div className="row">
            <button type="button" onClick={() => { set("invoice_via_agency", true); setQuestion(null); }}>{t("Rechnung an die Agentur", "Invoice to the agency")}</button>
            <button type="button" className="primary" onClick={() => void save(undefined, true)}>{t("So speichern", "Save like this")}</button>
          </div>
        </div>
      )}
      {live.hints.length > 0 && !question && <p className="muted">{live.hints.join(" ")}</p>}
      {error && !conflict && <p className="tk-error" role="alert">{error}</p>}
      {conflict && (
        <div className="notice stack" role="alert">
          <p><strong>{t("Dieser Eintrag wurde geändert.", "This entry has been changed.")}</strong> {t("Prüfe die aktuelle Version, bevor du deine Änderungen übernimmst. Beim Laden gehen deine Eingaben in diesem Fenster verloren.", "Check the current version before applying your changes. Loading discards your entries in this window.")}</p>
          <div className="row"><button type="button" className="primary" onClick={() => void onReload()}>{t("Aktuelle Version laden", "Load current version")}</button></div>
        </div>
      )}

      <div className="row between">
        <div className="row">
          {brand && represented.length > 0 && (
            <small>{t("Löschen geht erst, wenn keine Marke mehr über diese Agentur läuft (", "Deleting is only possible once no brand runs via this agency any more (")}{represented.map((b) => b.name).join(", ")}).</small>
          )}
          {brand && usage && usageTotal(usage) === 0 && represented.length === 0 && (
            <ConfirmAction
              danger label={t("Marke löschen", "Delete brand")} confirmLabel={t("Endgültig löschen", "Delete permanently")} busy={busy === "loeschen"}
              question={t(`„${brand.name}“ endgültig löschen? Kontakte und Notiz werden mitgelöscht. Das lässt sich nicht rückgängig machen.`, `Permanently delete “${brand.name}”? Contacts and note will be deleted too. This cannot be undone.`)}
              onConfirm={async () => { if (await run("loeschen", () => deleteBrand(api, brand.id), t("Marke gelöscht.", "Brand deleted."))) { onClose(); navigate("/agentur/marken"); } }}
            />
          )}
          {brand && usage && usageTotal(usage) > 0 && (
            <small>{t("Löschen geht nur ohne Kooperationen, Rechnungen, Verträge und Buchungen (", "Deleting is only possible without deals, invoices, contracts and bookings (")}{usage.deals} {t("Kooperationen, ", "deals, ")}{usage.invoices} {t("Rechnungen).", "invoices).")}</small>
          )}
        </div>
        <div className="row">
          <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
          <button className="primary" type="submit" form="marke-form" disabled={!!busy} aria-busy={busy === "speichern"}>
            {busy === "speichern" ? t("Wird gespeichert …", "Saving …") : brand ? t("Speichern", "Save") : t("Marke anlegen", "Create brand")}
          </button>
        </div>
      </div>
    </>
  );
}

/** Zuständigkeiten eines Kontakts als Kästchen (Rechnung gibt es je Marke nur einmal: wer sie bekommt, löst den bisherigen ab). */
function RolesField({ roles, onChange, idPrefix }: { roles: string[]; onChange: (r: string[]) => void; idPrefix: string }) {
  return (
    <div className="stack" role="group" aria-labelledby={`${idPrefix}-rollen`}>
      <small id={`${idPrefix}-rollen`}>{t("Zuständig für", "Responsible for")}</small>
      <div className="row">
        {CONTACT_ROLES.map((r) => (
          <label key={r.key} className="row">
            <input type="checkbox" checked={roles.includes(r.key)} onChange={(e) => onChange(e.target.checked ? [...roles, r.key] : roles.filter((x) => x !== r.key))} />
            {r.label}
          </label>
        ))}
      </div>
      <small>{t("Rechnung: genau ein Kontakt je Marke; die Rechnungs-E-Mail der Marke hat Vorrang. Vertrag: unterschreibt für die Marke. Insights: bekommt Freigaben.", "Invoice: exactly one contact per brand; the brand's invoice email takes precedence. Contract: signs for the brand. Insights: receives shares.")}</small>
    </div>
  );
}

/** Kontakte einer Marke pflegen (brand_contacts): anlegen, ändern, entfernen, Zuständigkeiten festlegen. */
export function ContactsEditor({ brandId, contacts }: { brandId: string; contacts: readonly ContactRow[] }) {
  const ws = useStaffWorkspace();
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<ContactForm>(() => contactFormFrom(null));
  const [errors, setErrors] = useState<FormErrors<ContactForm>>({});
  const { busy, error, run } = useAction();

  function start(c: ContactRow | null) {
    setEditing(c ? c.id : "neu");
    setForm(contactFormFrom(c));
    setErrors({});
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    const r = validateContact(form);
    setErrors(r.errors);
    if (!r.patch) return;
    const patch = r.patch;
    const id = editing && editing !== "neu" ? editing : undefined;
    if (await run("kontakt", () => saveContact(api, ws.org.org_id, brandId, patch, id), id ? t("Kontakt gespeichert.", "Contact saved.") : t("Kontakt hinzugefügt.", "Contact added."))) setEditing(null);
  }

  const editor = (
    <form className="panel stack" onSubmit={save} noValidate aria-label={editing === "neu" ? t("Kontakt hinzufügen", "Add contact") : t("Kontakt bearbeiten", "Edit contact")}>
      <div className="grid tk-oben">
        <TextField id="kf-name" label="Name" required value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} error={errors.name} />
        <TextField id="kf-rolle" label={t("Rolle", "Role")} placeholder={t("Marketing, Einkauf …", "Marketing, purchasing …")} value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))} />
        <TextField id="kf-mail" label={t("E-Mail", "Email")} type="email" inputMode="email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} error={errors.email} />
        <TextField id="kf-telefon" label={t("Telefon", "Phone")} type="tel" inputMode="tel" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
      </div>
      <RolesField roles={form.roles} onChange={(roles) => setForm((f) => ({ ...f, roles, is_invoice_recipient: roles.includes("invoice") }))} idPrefix="kf" />
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button className="primary" type="submit" disabled={busy === "kontakt"}>{busy === "kontakt" ? t("Wird gespeichert …", "Saving …") : t("Kontakt speichern", "Save contact")}</button>
        <button type="button" onClick={() => setEditing(null)}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );

  return (
    <div>
      {contacts.length === 0 && editing !== "neu" && <p className="muted">{t("Noch keine Kontakte hinterlegt.", "No contacts on file yet.")}</p>}
      {contacts.map((c) =>
        editing === c.id ? <div key={c.id}>{editor}</div> : (
          <div key={c.id} className="task">
            <div>
              <strong>{c.name}</strong>
              <p>{[c.role, c.email, c.phone].filter(Boolean).join("\u00a0· ") /* Trennpunkt bleibt am Wort davor */ || t("Keine Kontaktdaten", "No contact details")}</p>
            </div>
            <div className="row tk-kontakt-aktionen">
              {contactRoles(c).map((r) => <PillView key={r} pill={pill(CONTACT_ROLES.find((x) => x.key === r)?.label ?? r, r === "invoice" ? "info" : "neutral")} />)}
              <button type="button" onClick={() => start(c)} aria-label={t(`Kontakt ${c.name} bearbeiten`, `Edit contact ${c.name}`)}>{t("Bearbeiten", "Edit")}</button>
              <ConfirmAction
                label={t("Entfernen", "Remove")} confirmLabel={t("Ja, entfernen", "Yes, remove")} busy={busy === `weg-${c.id}`}
                question={t(`Kontakt „${c.name}“ entfernen?`, `Remove contact “${c.name}”?`)}
                onConfirm={() => run(`weg-${c.id}`, () => deleteContact(api, c.id), t("Kontakt entfernt.", "Contact removed."))}
              />
            </div>
          </div>
        ),
      )}
      {editing === "neu" ? editor : (
        <div className="row"><button type="button" onClick={() => start(null)}>{t("Kontakt hinzufügen", "Add contact")}</button></div>
      )}
      {error && editing === null && <p className="tk-error" role="alert">{error}</p>}
    </div>
  );
}
